import type { FC } from "react";

import { Button } from "../ui/button.tsx";
import {
  User,
  LogOutIcon,
  CreditCardIcon,
  Heart,
  CircleQuestionMark,
  Bell,
  Plug,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.tsx";
import { Link, NavLink, useNavigate } from "react-router";
import Text from "../Text";
import SearchBar from "../SearchBar/index.tsx";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice.ts";
import { signOut } from "aws-amplify/auth";
import { resetUser } from "@/store/userState.ts";
import { useTranslation } from "react-i18next";
import { triggerEvent } from "@/lib/utils.ts";

const Header: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sm } = useDetectSizeDevice();

  return (
    <div className="h-(--height-header) w-[100%] bg-[#FCFCFC] sm:bg-[white] flex items-center justify-between px-[24px] z-2 sm:z-1">
      {sm && <SearchBar />}
      <div className="sm:hidden flex items-center gap-[16px]">
        <img alt="krunsh logo" src={"/svg/krunsh.svg"} />
      </div>
      <div className="flex gap-[12px] items-center">
        {!sm && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondaryFilled"
                style={
                  sm
                    ? undefined
                    : {
                        borderRadius: "50%",
                        height: 38,
                        width: 38,
                      }
                }
              >
                <CircleQuestionMark color="#44546F" height={16} width={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="p-[16px] bg-[#FCFCFD] rounded-[12px] mr-[10px] mt-[10px]"
              align="start"
            >
              <DropdownMenuGroup>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://krunsh.crisp.help/${navigator.language.split("-")[0] === "fr" ? "fr" : "en"}/`}
                >
                  <DropdownMenuItem>
                    <Text fontSize={14} fontWeight={400}>
                      {t("helper_center")}
                    </Text>
                  </DropdownMenuItem>
                </a>

                <DropdownMenuItem
                  onClick={() => {
                    // @ts-ignore
                    $crisp.push(["do", "chat:open"]);
                    // @ts-ignore
                    $crisp.push(["do", "chat:show"]);
                  }}
                >
                  <Text fontSize={14} fontWeight={400}>
                    {t("chat_with_us")}
                  </Text>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {!sm && (
          <NavLink
            to="/alerts"
            className="h-[32px] w-[32px] flex items-center justify-center"
          >
            <Button
              className="h-[38px] w-[38px] p-[Opx] rounded-[50%]"
              variant="secondaryFilled"
            >
              <Bell strokeWidth={2.8} height={16} width={16} color="#44546F" />
            </Button>
          </NavLink>
        )}

        <NavLink
          to="/favorites"
          className="h-[32px] w-[32px] flex items-center justify-center"
        >
          <Button
            className="h-[38px] w-[38px] p-[Opx] rounded-[50%]"
            variant="secondaryFilled"
          >
            <Heart strokeWidth={2.8} height={16} width={16} color="#44546F" />
          </Button>
        </NavLink>

        <DropdownMenu>
          <DropdownMenuTrigger asChild className="sm:hidden">
            <Button
              className="h-[38px] w-[38px] p-[Opx] rounded-[50%]"
              variant="secondaryFilled"
            >
              <img src="/img/icon_menu_burger.png" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="p-[16px] bg-[#FCFCFD] rounded-[12px] mr-[10px] mt-[10px]"
            align="start"
          >
            <DropdownMenuGroup>
              <Link to="/profile">
                <DropdownMenuItem>
                  <User />
                  <Text fontSize={14} fontWeight={600}>
                    {t("profile")}
                  </Text>
                </DropdownMenuItem>
              </Link>
              <Link to="/subscriptions">
                <DropdownMenuItem>
                  <CreditCardIcon />
                  <Text fontSize={14} fontWeight={600}>
                    {t("subscription_and_payments")}
                  </Text>
                </DropdownMenuItem>
              </Link>
              <Link to="/connectors">
                <DropdownMenuItem>
                  <Plug />
                  <Text fontSize={14} fontWeight={600}>
                    Connecteurs
                  </Text>
                </DropdownMenuItem>
              </Link>
              <DropdownMenuItem
                onClick={async () => {
                  triggerEvent("User Logged Out");
                  await signOut();
                  resetUser();
                  navigate("/explorer/sign-in");
                }}
              >
                <LogOutIcon color="#AE2E24" />
                <Text fontSize={14} fontWeight={600} color="#AE2E24">
                  {t("log_out")}
                </Text>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default Header;
